import { isLoadBearing, type BlockDependency } from '@agavi/blocks';

import { scenarioFollowersOf } from './scenario-links';

/**
 * WHY A BLOCK CANNOT BE DELETED, or undefined when it can — §7.4, widened to references.
 *
 * Extracted from `task-workspace-page` so it can be tested at all. It was a closure inside a
 * three-thousand-line page with no test file, which was tolerable while it asked one question
 * about one task and is not now that it asks two about the whole experience.
 *
 * ## Two guards, and only one of them is about this task
 *
 * A scenario FOLLOWER is always in the same task as its leader, so that half reads the live
 * block list. A DRIVER is in an earlier task by definition, and a recall can be anywhere — so
 * the second half reads the experience-wide census, which is also the only thing that knows
 * which references are load-bearing.
 *
 * ## Where the line is, and whose call it was
 *
 * The owner's: *"if there are 10 chips that reference a block, it's a pain to delete all of
 * them before deleting this block. better to allow delete and have the chips go to error
 * state ... prose/equation failures are fine."* So the guard refuses exactly what breaks
 * SILENTLY — a driven link, which leaves a block asking a question nobody chose, and a branch
 * condition, which takes a branch nobody wrote. Everything else is allowed to break where the
 * teacher can see it.
 *
 * `t` and `labelOf` are passed rather than imported, the arrangement `dndA11y` already uses:
 * it keeps the decision testable without standing up i18n, and keeps the page the only place
 * that knows how a block type is spelled.
 */
export function blockDeleteReason(blockId: string, ctx: {
  /** This task's live blocks — the scenario half, which is in-task by construction. */
  blocks: { id: string; name?: string; type: string; data?: unknown }[];
  /** The whole experience, for naming a dependent that is not in this task. */
  tasks: { id: string; title?: string | null; blocks?: { id: string; name?: string | null; type: string }[] }[];
  currentTaskId: string | undefined;
  /**
   * `dependentsByBlock`'s output, PASSED IN rather than computed here.
   *
   * This is called once per card on every render, and the census walks every block's data in
   * the experience. Computing it here would make the guard quadratic on the render path for
   * an answer that is identical for every card.
   */
  dependents: Map<string, BlockDependency[]>;
  t: (key: string, vars?: Record<string, unknown>) => string;
  labelOf: (type: string) => string;
}): string | undefined {
  const followers = scenarioFollowersOf(blockId, ctx.blocks);
  if (followers.length > 0) {
    return ctx.t('task.errors.blockFollowedBy', {
      names: followers.map(b => b.name || ctx.labelOf(b.type)).join(', '),
    });
  }

  const blocking = (ctx.dependents.get(blockId) ?? []).filter(isLoadBearing);
  if (blocking.length === 0) return undefined;

  const nameOf = (dep: BlockDependency): string => {
    const task = ctx.tasks.find(tk => tk.id === dep.inTaskId);
    const block = task?.blocks?.find(b => b.id === dep.inBlockId);
    const name = block?.name || ctx.labelOf(block?.type ?? '');
    /* The task only where it is ELSEWHERE. "Distance in Measure it" is what a teacher needs
       to go and find it; the same suffix on a block three inches away is noise. */
    return dep.inTaskId === ctx.currentTaskId
      ? name
      : ctx.t('task.errors.dependentInTask', { block: name, task: task?.title ?? '' });
  };

  /* DISTINCT. One dependent can hold several — a block driven by this AND branching on it is
     one thing to go and disconnect, not the same name twice. */
  const names = [...new Set(blocking.map(nameOf))];
  return ctx.t('task.errors.blockDependedOn', { names: names.join(', ') });
}
